// StartupPlugins.tsx
import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Stripe } from '@am3nd-org/pointcraft-stripe';

import { useSystemStore } from 'src/store/SystemStore';
import { DeviceType } from 'src/store/SystemStore/types';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { STRIPE_PUBLISHABLE_KEY } from 'src/config-global';
import { useDeviceContextStore } from 'src/store/DeviceContext';
import { selectCurrentUser } from 'src/store/DeviceSync/selectors';
import { printerHealthTrackingApplies } from 'src/services/printing';

import { PrinterHelperHost } from 'src/components/universal-checkout/shared/printer-readiness-dialog';

import { PrintingInit } from './PrintingInit';

type StartupPluginsProps = {
  children: React.ReactNode;
};

export function StartupPlugins({ children }: StartupPluginsProps) {
  const setCurrentDeviceType = useSystemStore((state) => state.setCurrentDeviceType);

  useEffect(() => {
    if (!Capacitor.isPluginAvailable('Stripe')) return;

    // Establish the ordinary app default once. Provider-created online
    // payments reinitialize immediately before presentation with the
    // publishable key returned for that specific intent.
    Stripe.initialize({ publishableKey: STRIPE_PUBLISHABLE_KEY }).catch(() => {
      console.error('Stripe SDK initialization failed.');
    });
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      setCurrentDeviceType(DeviceType.MOBILE);
    }
  }, [setCurrentDeviceType]);

  // Set up Sentry user context tracking
  useEffect(() => {
    const unsubscribeUser = useDeviceSyncStore.subscribe((state) => {
      const currentUser = selectCurrentUser(state);
      const business = state.businessData;
      if (currentUser && business) {
        Sentry.setUser({
          id: currentUser.id,
          email: currentUser.profile.email || undefined,
        });

        Sentry.setTags({
          business_id: business.id,
          business_name: business.name,
          device_location_id: useDeviceContextStore.getState().businessLocationId || 'unknown',
          offline_mode: business.configurations.offlineModeEnabled,
        });
      } else {
        Sentry.setUser(null);
      }
    });

    const unsubscribeSystem = useSystemStore.subscribe((state) => {
      Sentry.setTags({
        device_type: state.currentDeviceType,
        platform: Capacitor.getPlatform(),
        is_native: Capacitor.isNativePlatform(),
        app_version: state.appVersion.current,
        ble_reader_connected: Boolean(state.bleReader),
      });
    });

    const unsubscribeDeviceContext = useDeviceContextStore.subscribe((state) => {
      Sentry.setTags({
        business_device_id: state.businessDeviceId || 'unknown',
        device_location_id: state.businessLocationId || 'unknown',
        terminal_id: state.terminalId || 'unknown',
        device_context_environment: state.environment || 'unknown',
      });
    });

    return () => {
      unsubscribeUser();
      unsubscribeSystem();
      unsubscribeDeviceContext();
    };
  }, []);

  // The printer helper is mounted wherever a real printer can exist, which is a
  // capability and not a `DeviceType` (SITE_TRIAGE bucket B1): an Electron
  // register is `DESKTOP` and has a Star printer on the LAN, so it needs the
  // troubleshoot dialog exactly as a phone does. `LiveFooter` and
  // `chrome-footer` gate their "printer needs attention" button on the same
  // predicate, so the button and the dialog it opens cannot diverge.
  const printerHelperAvailable = printerHealthTrackingApplies();
  return (
    <>
      {/* Mounted on every platform: the web build uses the StarPrint mock, and the
          central printing service needs the active printer hydrated everywhere. */}
      <PrintingInit />
      {printerHelperAvailable && <PrinterHelperHost />}
      {children}
    </>
  );
}
